import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, XCircle, ExternalLink, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, BrandButton } from "../../components/tenant/TenantShell";
import { tapi, apiErr, mainSiteUrl } from "../../lib/tenant";
import { PREQUAL_QUESTIONS, gateReason, savePrequal } from "../../lib/lpoPrequal";
import { KesInput } from "../../components/tenant/KesInput";

const inputCls = "w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-base text-neutral-900 outline-none focus:border-[var(--tenant)] focus:ring-2 focus:ring-[var(--tenant)]/20";

function YesNo({ value, onChange, id }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`prequal-${id}-${v}`}
          className={`rounded-xl border px-4 py-3 text-sm font-bold transition-colors ${value === v ? "text-white" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}
          style={value === v ? { background: "var(--tenant)", borderColor: "var(--tenant)" } : undefined}>{l}</button>
      ))}
    </div>
  );
}

function Choice({ options, value, onChange, id }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`prequal-${id}-${o.v}`}
          className={`rounded-xl border px-4 py-3 text-sm font-bold transition-colors ${value === o.v ? "text-white" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}
          style={value === o.v ? { background: "var(--tenant)", borderColor: "var(--tenant)" } : undefined}>{o.l}</button>
      ))}
    </div>
  );
}

function Answer({ q, value, onChange }) {
  if (q.type === "yesno") return <YesNo id={q.id} value={value} onChange={onChange} />;
  if (q.type === "choice") return <Choice id={q.id} options={q.options} value={value} onChange={onChange} />;
  if (q.type === "date") return <input type="date" value={value || ""} max={new Date().toISOString().slice(0, 10)} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`prequal-${q.id}-input`} />;
  if (q.type === "kes") return <KesInput value={value} onChange={onChange} className={inputCls} testid={`prequal-${q.id}-input`} />;
  return (
    <div className="relative"><input type="number" inputMode="numeric" min="0" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="e.g. 60" className={`${inputCls} pr-16`} data-testid={`prequal-${q.id}-input`} />
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-neutral-500">days</span></div>);
}

function Outcome({ qualified, reason, onSignup, config }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8" data-testid={qualified ? "prequal-pass" : "prequal-fail"}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${qualified ? "text-white" : "bg-neutral-100 text-neutral-500"}`} style={qualified ? { background: "var(--tenant)" } : undefined}>
        {qualified ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
      </span>
      <h1 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl" data-testid="prequal-outcome-headline">
        {qualified ? "You prequalify for funding from Lasiri Capital." : "You currently do not qualify for Lasiri funding."}
      </h1>
      <p className="mt-3 text-base text-neutral-600" data-testid="prequal-outcome-message">
        {qualified ? "Sign up to continue your application." : "Find out who else can fund your business."}
      </p>
      {!qualified && reason && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border-l-4 border-amber-400 bg-amber-50 px-4 py-3" data-testid="prequal-fail-reason">
          <Info size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700">Why you didn't qualify</p>
            <p className="mt-0.5 text-sm font-semibold text-amber-900">{reason}</p>
          </div>
        </div>
      )}
      <div className="mt-6">
        {qualified ? (
          <BrandButton onClick={onSignup} data-testid="prequal-signup-btn">Sign up to continue <ArrowRight size={16} /></BrandButton>
        ) : (
          <a href={mainSiteUrl("/")} data-testid="prequal-clinq-btn"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5" style={{ background: "var(--tenant)" }}>
            Find out who else can fund your business <ExternalLink size={14} />
          </a>
        )}
      </div>
      {qualified && <p className="mt-4 text-xs text-neutral-500">Pre-qualification is indicative. {config.name} confirms eligibility after reviewing your LPO and documents.</p>}
    </div>
  );
}

export default function TenantPrequalify() {
  const { sub, config, to } = useTenant();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null); // { qualified, reason }
  const [busy, setBusy] = useState(false);
  const q = PREQUAL_QUESTIONS[step];
  const value = answers[q?.id];
  const filled = value !== undefined && value !== "";

  const record = async (qualified, failedGate, reason) => {
    setBusy(true);
    try {
      const { data } = await tapi.post(`/t/${sub}/prequal`, { answers, qualified, failed_gate: failedGate || null });
      if (qualified) savePrequal(sub, { prequal_id: data.prequal_id, passed: true, answers });
    } catch (e) {
      if (qualified) { toast.error(apiErr(e)); setBusy(false); return; }
    }
    setBusy(false);
    setResult({ qualified, reason });
  };

  const next = async () => {
    if (!filled) return;
    const reason = gateReason(q.id, value);
    if (reason && q.type !== "kes" && q.type !== "days") return record(false, q.id, reason);
    if (reason) {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) { toast.error(reason); return; }
      return record(false, q.id, reason);
    }
    if (step + 1 < PREQUAL_QUESTIONS.length) setStep(step + 1);
    else record(true, null, null);
  };

  if (result) return <TenantShell><div className="mx-auto max-w-xl"><Outcome {...result} config={config} onSignup={() => navigate(to("/start"))} /></div></TenantShell>;

  return (
    <TenantShell>
      <div className="mx-auto max-w-xl" data-testid="tenant-prequal">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }}>{config.name} · Eligibility check</p>
        <div className="mb-4 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-neutral-200"><div className="h-full rounded-full transition-all" style={{ width: `${((step + 1) / PREQUAL_QUESTIONS.length) * 100}%`, background: "var(--tenant)" }} /></div>
          <span className="text-xs font-semibold text-neutral-500" data-testid="prequal-progress">{step + 1} / {PREQUAL_QUESTIONS.length}</span>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
          <h1 className="text-xl font-extrabold leading-snug tracking-tight sm:text-2xl" data-testid="prequal-question">{q.q}</h1>
          {q.help && <p className="mt-2 text-sm text-neutral-500">{q.help}</p>}
          <div className="mt-5"><Answer q={q} value={value} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} /></div>
          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={() => (step === 0 ? navigate(to("/")) : setStep(step - 1))} className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-500 hover:text-neutral-800" data-testid="prequal-back">
              <ArrowLeft size={14} /> Back
            </button>
            <BrandButton onClick={next} disabled={!filled || busy} data-testid="prequal-next">
              {busy ? <Loader2 size={16} className="animate-spin" /> : step + 1 === PREQUAL_QUESTIONS.length ? "See my result" : "Next"} {!busy && <ArrowRight size={16} />}
            </BrandButton>
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-neutral-500">Takes about 2 minutes · No sign-up needed to check</p>
      </div>
    </TenantShell>
  );
}
